'use client';

import Link from 'next/link';
import { User, ShoppingCart, LogOut, LayoutDashboard, ShieldCheck, ChevronDown } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCartStore } from '@/core/hooks/cart/useCartStore';
import { useAuth } from '@/contexts/authContext';
import { usePathname } from 'next/navigation';
import { useDictionary } from '@/core/hooks/use-dictionary';
import { HeaderNavSearch } from './HeaderNavSearch';
import SelectLanguage from '@/components/account/SelectLanguage';
import HeaderSelecetLanguage from './HeaderSelecetLanguage';

export default function HeaderNavAction() {
  const { cart } = useCartStore();
  const { dictionary } = useDictionary()
  const { state, clearAuth, hasPermission } = useAuth();
  const pathname = usePathname();

  const totalItems = cart?.items?.reduce(
    (sum, item) => sum + (item.quantity || 0),
    0
  );

  const isLoggedIn = state?.isAuthenticated;
  const user = state?.user;

  // Create login URL with return parameter
  const loginHref = `/login?returnUrl=${encodeURIComponent(pathname)}`;

  return (
    <div className="flex justify-end items-center gap-4">


      {/* 🛒 Cart */}
      <div className="relative">
        <Link href="/cart" aria-label="Shopping cart">
          <svg className='w-[0.9rem]' xmlns="http://www.w3.org/2000/svg" width="18" height="21" viewBox="0 0 18 21" fill="none">
            <path d="M17.1667 5.5H0.5V20.5H17.1667V5.5Z" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M5.49951 7.16668V3.83334C5.49951 2.94928 5.8507 2.10143 6.47582 1.47631C7.10095 0.85119 7.94879 0.5 8.83285 0.5C9.71691 0.5 10.5648 0.85119 11.1899 1.47631C11.815 2.10143 12.1662 2.94928 12.1662 3.83334V7.16668" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Link>

        {totalItems > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-medium rounded-full px-[5px] py-px leading-none">
            {totalItems}
          </span>
        )}
      </div>
      {/* 👤 User Section */}
      <div className='hidden lg:block'>
        {isLoggedIn ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                aria-label="User menu"
                className="flex items-center justify-center hover:text-primary transition"
              >
                <User className="w-5 h-5 stroke-1" />
                <ChevronDown className="size-4 stroke-1" />
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              align="center"
              sideOffset={6}
              className="w-36 p-1.5 text-sm shadow-md border border-gray-100"
            >
              <DropdownMenuLabel className="px-2 py-1.5 text-[13px] text-primary-600 truncate">
                {user?.full_name || 'کاربر'}
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="my-1" />

              <DropdownMenuItem
                asChild
                className="px-2 py-1.5 cursor-pointer text-[13px] hover:bg-gray-100"
              >
                <Link href="/profile" className="flex items-center gap-2">
                  {dictionary.forms.profile}
                  <User className="size-4 text-primary-900 stroke-1" />
                </Link>
              </DropdownMenuItem>

              <DropdownMenuItem
                asChild
                className="px-2 py-1.5 cursor-pointer text-[13px] hover:bg-gray-100"
              >
                <Link href="/dashboard" className="flex items-center gap-2">
                  {dictionary.dashboard.title}
                  <LayoutDashboard className="size-4 text-primary-900 stroke-1" />
                </Link>
              </DropdownMenuItem>

              {hasPermission("AccessToAdmin") && (
                <DropdownMenuItem
                  asChild
                  className="px-2 py-1.5 cursor-pointer text-[13px] hover:bg-gray-100"
                >
                  <Link href="/admin" className="flex items-center gap-2">
                    {dictionary.ui.profile.adminPanel}
                    <ShieldCheck className="size-4 text-primary-900 stroke-1" />
                  </Link>
                </DropdownMenuItem>
              )}

              <DropdownMenuSeparator className="my-1" />

              <DropdownMenuItem
                onClick={clearAuth}
                className="px-2 py-1.5 cursor-pointer text-[13px] text-red-600 hover:bg-red-50 focus:text-red-600"
              >
                {dictionary.common.logout}
                <LogOut className="size-4 text-red-600 stroke-1" />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Link href={loginHref} aria-label="User login">
            <User className="w-5 h-5 stroke-1" />
          </Link>
        )}
      </div>
      <HeaderNavSearch />
      <div className='max-lg:hidden h-4.5'>
        <HeaderSelecetLanguage variant="mini" />
      </div>
    </div>
  );
}